// src/features/orders/hooks/useOrders.ts
import { useMemo } from 'react';
import type { Order } from '../types/orders.types';

export interface UseOrdersResult {
  orders: Order[];
  isLoading: boolean;
  isEmpty: boolean;
}

/**
 * Data-access abstraction for the Orders feature.
 *
 * No representative-scoped Orders endpoint exists on the backend yet
 * (only Services, Customers, and Support are exposed today). Per the
 * "don't invent endpoints, don't fabricate data" rule, this hook
 * returns an empty result instead of calling a non-existent endpoint
 * or falling back to sample data — the page renders its existing
 * EmptyState UI as a result.
 *
 * The moment a representative-scoped orders endpoint is available,
 * swap the body for a real fetch call; the `UseOrdersResult` contract
 * consumed by OrdersPage won't need to change.
 */
export function useOrders(): UseOrdersResult {
  const orders = useMemo<Order[]>(() => [], []);
  const isLoading = false;
  const isEmpty = true;

  return { orders, isLoading, isEmpty };
}
